import { Link, useParams } from 'react-router-dom';
import useSeo from '@/hooks/useSeo';
import { dossiers } from '@/mocks/dossiers';
import { site } from '@/mocks/site';
import { getDossierById, relatedDossiers } from '@/utils/dossiers';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import DossierCard from '@/components/feature/DossierCard';
import NotFound from '@/pages/NotFound';
import ArticleHeader from './components/ArticleHeader';
import ArticleBody from './components/ArticleBody';
import TimelineSection from './components/TimelineSection';
import ForeshadowSection from './components/ForeshadowSection';
import ConnectionSection from './components/ConnectionSection';
import DossierToc from './components/DossierToc';

export default function DossierDetail() {
  const { id } = useParams<{ id: string }>();
  const dossier = id ? getDossierById(id) : undefined;

  useSeo({
    title: dossier
      ? `${dossier.title} - 爆料看点深度拆解`
      : '档案不存在 - 爆料看点',
    description: dossier
      ? `${dossier.summary} 本篇围绕${dossier.question}展开，包含时间线关键节点、被忽略的伏笔与事件隐藏关联梳理。`
      : '该拆解档案暂不存在，可返回拆解档案库浏览其他内容。',
    keywords: dossier ? `爆料看点,${dossier.tags.join(',')},事件隐藏关联,时间线关键节点` : '爆料看点,拆解档案',
    path: `/dossier/${id ?? ''}`,
    jsonLd: dossier
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: dossier.title,
            description: dossier.summary,
            inLanguage: 'zh-CN',
            datePublished: dossier.date,
            dateModified: dossier.date,
            articleSection: dossier.dimension,
            keywords: dossier.tags.join(','),
            author: { '@type': 'Organization', name: dossier.author },
            publisher: { '@type': 'Organization', name: site.name },
            mainEntityOfPage: `https://baoliaokandian.site/dossier/${dossier.id}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://baoliaokandian.site/' },
              { '@type': 'ListItem', position: 2, name: '拆解档案', item: 'https://baoliaokandian.site/dossiers' },
              { '@type': 'ListItem', position: 3, name: dossier.title, item: `https://baoliaokandian.site/dossier/${dossier.id}` },
            ],
          },
        ]
      : undefined,
  });

  if (!dossier) {
    return <NotFound />;
  }

  const related = relatedDossiers(dossier, 3);
  const currentIndex = dossiers.findIndex((item) => item.id === dossier.id);
  const prev = currentIndex > 0 ? dossiers[currentIndex - 1] : null;
  const next = currentIndex < dossiers.length - 1 ? dossiers[currentIndex + 1] : null;

  return (
    <>
      <div className="mx-auto w-full max-w-[1200px] px-4 pt-8 md:px-8">
        <Breadcrumbs
          items={[
            { label: '首页', to: '/' },
            { label: '拆解档案', to: '/dossiers' },
            { label: dossier.title },
          ]}
        />
      </div>

      <div className="mt-6">
        <ArticleHeader dossier={dossier} />
      </div>

      <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <article className="lg:col-span-8">
            <ArticleBody dossier={dossier} />
            <div className="mt-14 space-y-14">
              <TimelineSection dossier={dossier} />
              <ForeshadowSection dossier={dossier} />
              <ConnectionSection dossier={dossier} />
            </div>
          </article>

          <div className="lg:col-span-4">
            <DossierToc dossier={dossier} />
          </div>
        </div>

        <nav aria-label="上一篇与下一篇" className="mt-16 grid grid-cols-1 gap-4 border-t border-background-200 pt-8 md:grid-cols-2">
          {prev ? (
            <Link
              to={`/dossier/${prev.id}`}
              className="group rounded-lg border border-background-200 bg-background-100 p-5 transition-colors hover:border-primary-300"
            >
              <span className="inline-flex items-center gap-1.5 font-label text-xs text-foreground-500">
                <i className="ri-arrow-left-line" />
                上一篇
              </span>
              <p className="mt-2 font-heading text-sm font-semibold leading-snug text-foreground-900 group-hover:text-primary-600">
                {prev.title}
              </p>
            </Link>
          ) : (
            <span />
          )}
          {next && (
            <Link
              to={`/dossier/${next.id}`}
              className="group rounded-lg border border-background-200 bg-background-100 p-5 text-right transition-colors hover:border-primary-300 md:text-right"
            >
              <span className="inline-flex items-center gap-1.5 font-label text-xs text-foreground-500">
                下一篇
                <i className="ri-arrow-right-line" />
              </span>
              <p className="mt-2 font-heading text-sm font-semibold leading-snug text-foreground-900 group-hover:text-primary-600">
                {next.title}
              </p>
            </Link>
          )}
        </nav>

        <section className="mt-16">
          <div className="flex flex-col gap-3 border-b border-background-200 pb-6 sm:flex-row sm:items-end sm:justify-between">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关拆解档案</h2>
            <Link
              to="/dossiers"
              className="inline-flex items-center gap-1.5 whitespace-nowrap font-label text-sm text-foreground-700 hover:text-primary-600"
            >
              全部档案
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {related.map((item, index) => (
              <DossierCard key={item.id} dossier={item} order={index} />
            ))}
          </div>
        </section>
      </div>
    </>
  );
}